import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { differenceInCalendarDays, parseISO } from "date-fns";
import {
  ArrowUpRight,
  FlaskConical,
  ListChecks,
  PencilLine,
  RotateCcw,
  Sparkles,
  Target,
  TrendingUp,
} from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Button } from "@/platform/ui/button";
import { useSession } from "@/platform/auth/use-session";
import { useProfile } from "@/platform/auth/use-profile";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import { TrophySpot } from "@/platform/ui/illustrations/spot";
import { SubscriptionBanner } from "@/platform/billing/subscription-banner";
import { fetchUspProfile } from "@/tenants/usp/features/profile/api";
import { countDueUspReviews } from "@/tenants/usp/features/review/api";
import { generateUspPlan } from "@/tenants/usp/features/study-plan/api";

const ACTIONS = [
  { to: "/usp/1afase", icon: Target, label: "Simulado 1ª fase", tone: "accent" },
  { to: "/usp/2afase", icon: FlaskConical, label: "Discursivas 2ª fase", tone: "pink" },
  { to: "/usp/redacao", icon: PencilLine, label: "Redação Fuvest", tone: "coral" },
  { to: "/usp/revisar", icon: RotateCcw, label: "Revisar", tone: "mint" },
  { to: "/usp/plano", icon: ListChecks, label: "Plano da semana", tone: "sky" },
  { to: "/usp/evolucao", icon: TrendingUp, label: "Evolução", tone: "yellow" },
];

export function UspPainelPage() {
  const { session } = useSession();
  const { profile } = useProfile();
  const userId = session?.user.id;

  const uspProfileQuery = useQuery({
    queryKey: ["usp", "profile"],
    queryFn: () => fetchUspProfile(userId!),
    enabled: !!userId,
  });

  const dueReviewsQuery = useQuery({
    queryKey: ["usp", "due-reviews-count"],
    queryFn: () => countDueUspReviews(userId!),
    enabled: !!userId,
  });

  const planQuery = useQuery({
    queryKey: ["usp", "plan"],
    queryFn: () => generateUspPlan(userId!),
    enabled: !!userId,
  });

  const uspProfile = uspProfileQuery.data;
  const firstName = (profile?.display_name ?? profile?.full_name ?? "estudante").split(" ")[0];
  const examDate = uspProfile?.exam_date_fase1 ? parseISO(uspProfile.exam_date_fase1) : null;
  const daysToExam = examDate ? Math.max(0, differenceInCalendarDays(examDate, new Date())) : null;
  const dueReviews = dueReviewsQuery.data ?? 0;
  const topBlock = planQuery.data?.blocks[0];

  return (
    <div className="relative overflow-hidden bg-mesh min-h-[calc(100vh-56px)]">
      <Blob color="sky" className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-20 blur-2xl animate-blob" />
      <SoftStar color="mint" className="pointer-events-none absolute right-10 top-32 w-16 opacity-50 animate-float" />

      <Container size="wide" className="relative py-12 md:py-16">
        <div className="mb-8">
          <SubscriptionBanner tenant="usp" />
        </div>

        {/* HERO */}
        <header className="animate-fade-up grid gap-6 md:grid-cols-[1fr_auto] md:items-end">
          <div>
            <Badge variant="accentSolid" className="w-fit gap-2">
              <Sparkles className="size-3" />
              FuvestPro USP
            </Badge>
            <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold leading-tight tracking-[var(--text-display-md--letter-spacing)] text-balance">
              Oi, <span className="text-gradient-accent">{firstName}</span>.
            </h1>
            <p className="mt-2 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
              {!uspProfile?.onboarding_completed
                ? "Complete o onboarding para calibrar seu plano."
                : topBlock
                  ? `Foco da semana: ${topBlock.label}.`
                  : "Faça um simulado para calibrar seu plano."}
            </p>
          </div>
          {daysToExam != null && (
            <div className="flex items-center gap-4 rounded-xl p-5 text-white shadow-[var(--shadow-md)]" style={{ background: "linear-gradient(135deg, var(--color-accent), var(--color-accent-deep))" }}>
              <TrophySpot className="size-14" />
              <div>
                <p data-tabular="true" className="font-display text-[length:var(--text-display-md)] font-bold leading-none">
                  {daysToExam}
                </p>
                <p className="mt-1 text-[length:var(--text-caption)] uppercase tracking-wide opacity-90">
                  dias até a 1ª fase
                </p>
              </div>
            </div>
          )}
        </header>

        {!uspProfile?.onboarding_completed && (
          <div className="mt-8 rounded-2xl border-2 border-[--color-accent]/30 bg-[color:var(--color-accent-soft)] p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="font-display text-[length:var(--text-body-lg)] font-semibold">
              Configure seu curso-alvo e datas da prova.
            </p>
            <Button asChild size="lg" variant="primary">
              <Link to="/usp/onboarding">
                Completar onboarding
                <ArrowUpRight className="size-4" />
              </Link>
            </Button>
          </div>
        )}

        {/* Stats rápidos */}
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <StatCard label="Revisões pendentes" value={String(dueReviews)} hint={dueReviews > 0 ? "questões esperando" : "tudo em dia"} to="/usp/revisar" />
          <StatCard label="Foco prioritário" value={topBlock?.label ?? "—"} hint={topBlock ? `${topBlock.suggestedHours}h sugeridas` : "faça um simulado"} to="/usp/plano" />
          <StatCard label="Curso-alvo" value={uspProfile?.curso_alvo ? cursoLabel(uspProfile.curso_alvo) : "—"} hint="ajustável no onboarding" to="/usp/onboarding" />
        </div>

        {/* Ações */}
        <section className="mt-10">
          <h2 className="font-display text-[length:var(--text-body-lg)] font-semibold mb-4">O que você quer fazer?</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ACTIONS.map(({ to, icon: Icon, label, tone }) => (
              <Link
                key={to}
                to={to}
                className="group flex items-center gap-4 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-5 shadow-[var(--shadow-sm)] lift"
              >
                <span
                  className="chip size-11"
                  style={{
                    backgroundColor: `var(--color-${tone}-soft)`,
                    color: `var(--color-${tone}-deep)`,
                  }}
                >
                  <Icon className="size-5" aria-hidden />
                </span>
                <span className="flex-1 font-display text-[length:var(--text-body)] font-semibold">{label}</span>
                <ArrowUpRight className="size-4 text-[color:var(--color-muted-foreground)] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            ))}
          </div>
        </section>
      </Container>
    </div>
  );
}

function StatCard({ label, value, hint, to }: { label: string; value: string; hint: string; to: string }) {
  return (
    <Link to={to} className="rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-5 shadow-[var(--shadow-sm)] lift">
      <p className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
        {label}
      </p>
      <p className="mt-1.5 font-display text-[length:var(--text-display-sm)] font-semibold leading-tight capitalize" style={{ color: "var(--color-accent-deep)" }}>
        {value}
      </p>
      <p className="mt-0.5 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">{hint}</p>
    </Link>
  );
}

function cursoLabel(curso: string): string {
  return curso.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
